import type { ThemeProps, ThemeTokens } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import type { SprintState } from '@atlassian/jira-forge-ui-types/src/common/types/extension-data.tsx';
import { SPRINT_STATE_ACTIVE, SPRINT_STATE_FUTURE } from '../../model/constants.tsx';
import type { CardEntryGroup, CardId } from '../../model/types.tsx';

export type ButtonTheme = {
	default?: {
		background?: {
			default?: {
				light?: string;
				dark?: string;
			};
			hover?: {
				light?: string;
				dark?: string;
			};
		};
	};
};

// NOTE: Traverse theme structure to get the styles for the current combination of props as suggested here: https://atlassian.design/components/button/theming-guide#creating-styling-for-button-with-an-object-structure
export const applyAKButtonTheme =
	(buttonThemeOverrides: ButtonTheme) =>
	(
		currentTheme: (currentThemeProps: ThemeProps) => ThemeTokens,
		themeProps: ThemeProps,
	): ThemeTokens => {
		const { buttonStyles, ...rest } = currentTheme(themeProps);
		const { appearance, state, mode } = themeProps;
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"" | Appearance' can't be used to index type 'ButtonTheme'.
		const customThemeAppearance = buttonThemeOverrides[`${appearance || ''}`];

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let customStyles: Record<string, any> = {};

		if (customThemeAppearance) {
			// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'Record<string, any>'.
			customStyles = Object.keys(customThemeAppearance).reduce<ThemeTokens>(
				(acc: ThemeTokens, key: string) => {
					const value = customThemeAppearance[key];

					if (value[state]) {
						// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'ThemeTokens'.
						acc[key] = value[state][mode];
					}

					return acc;
				},
				// @ts-expect-error - TS2322 - Type 'null' is not assignable to type 'CSSObject'. | TS2322 - Type 'null' is not assignable to type 'CSSObject'.
				{ buttonStyles: null, spinnerStyles: null },
			);
		}
		return {
			...rest,
			buttonStyles: {
				...buttonStyles,
				...customStyles,
			},
		};
	};

const darkerButtonThemeOverrides: ButtonTheme = {
	default: {
		background: {
			default: { light: token('color.background.neutral') },
			hover: { light: token('color.background.neutral.hovered') },
		},
	},
};

export const darkerButtonTheme = applyAKButtonTheme(darkerButtonThemeOverrides);

export const isBottomOfList = (
	sourceCardList: string,
	destinationCardList: string,
	sourceCardIndex: number,
	destinationCardIndex: number,
	destinationCardListCardOrder: CardId[],
	selectedCards: CardId[],
): boolean => {
	const isBottomOfOtherList =
		sourceCardList !== destinationCardList &&
		destinationCardIndex === destinationCardListCardOrder.length;

	if (isBottomOfOtherList) {
		return true;
	}

	const isMovedFromAbove = sourceCardIndex < destinationCardIndex;

	// Is true when the destinationCardIndex is the last index in the list OR
	// ever card below this index is a selected card
	const isBottomOfSameList =
		sourceCardList === destinationCardList &&
		(destinationCardIndex === destinationCardListCardOrder.length - 1 ||
			destinationCardListCardOrder
				.slice(destinationCardIndex + (isMovedFromAbove ? 1 : 0))
				.every((cardId: CardId) => selectedCards.includes(cardId)));

	return isBottomOfSameList;
};

export const isBottomOfCardGroup = (
	cardGroup: CardEntryGroup,
	sourceCardIndex: number,
	destinationCardIndex: number,
	destinationCardListCardOrder: number[],
	selectedCards: CardId[],
) => {
	const currentCardAtDestinationIndex = destinationCardListCardOrder[destinationCardIndex];
	const isDestinationBottomOfCardGroup =
		currentCardAtDestinationIndex === cardGroup.cardIds[cardGroup.cardIds.length - 1];

	if (isDestinationBottomOfCardGroup) {
		return true;
	}

	const isMovedFromAbove = sourceCardIndex < destinationCardIndex;
	const destinationCardIndexWithinCardGroup = cardGroup.cardIds.findIndex(
		(id) => id === currentCardAtDestinationIndex,
	);

	// Is considered bottom of card group when every
	// card beneath destinationCardIndex is selected
	return cardGroup.cardIds
		.slice(destinationCardIndexWithinCardGroup + (isMovedFromAbove ? 1 : 0))
		.every((cardId) => selectedCards.includes(cardId));
};

export const getBeforeCardId = (
	sourceCardList: string,
	destinationCardList: string,
	sourceCardIndex: number,
	destinationCardIndex: number,
	destinationCardListCardOrder: CardId[],
	selectedCards: CardId[],
): CardId => {
	let cardIndex = destinationCardIndex;

	const correction =
		sourceCardList === destinationCardList && sourceCardIndex < destinationCardIndex ? 1 : 0;

	cardIndex = destinationCardListCardOrder.findIndex((cardId: CardId, index: number) =>
		index < cardIndex + correction ? false : !selectedCards.includes(cardId),
	);

	return destinationCardListCardOrder[cardIndex];
};

/**
 * We should show the transition zone when:
 *  user is dragging something
 *  AND the draggable is from another card-list
 *  AND one of these is true:
 *      cardMoveDisabled (If JQL defines a custom order, you can't rerank cards, but you can move them to another sprint.)
 *        AND the draggable is not a subtask (unless this is CMP Kanban, then that's ok)
 *      OR
 *      dragging a subtask AND this is a CMP Kanban backlog
 */
export const shouldShowTransitionZone = (
	isDragging: boolean,
	isCurrentCardListDragSource: boolean,
	cardMoveDisabled: boolean,
	isDraggingSubtask: boolean,
	isCmpKanbanBacklog: boolean,
) =>
	isDragging &&
	!isCurrentCardListDragSource &&
	((cardMoveDisabled && (!isDraggingSubtask || isCmpKanbanBacklog)) ||
		(isDraggingSubtask && isCmpKanbanBacklog));

// Map a sprint state to lowercase before including it in the Forge context
export const getSprintState = (state: string | undefined): Lowercase<SprintState> | undefined => {
	if (!state) {
		return undefined;
	}

	switch (state) {
		case SPRINT_STATE_FUTURE:
			return 'future';
		case SPRINT_STATE_ACTIVE:
			return 'active';
		default:
			return 'future';
	}
};
